import { useState } from "react";
import { Target, Trash2, Star } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import ConfirmModal from "../ConfirmModal";
import PhaseChip from "../PhaseChip";

/**
 * LeadsBlock — list of leads tagged to a customer.
 *
 * Each row is a <Link> straight to /leads/:id (the full detail page).
 * NO mini-popup with half-info — that pattern is forbidden by the
 * customer-profile-must-navigate rule.
 *
 * Delete is a separate trash icon next to each row, gated by ConfirmModal.
 */
export default function LeadsBlock({ leads, canEdit, onChanged, defaultOpen = false, isPortal = false }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/leads/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <FoldBlock
      icon={Target}
      iconColor="#a78bfa"
      title="Leads"
      count={leads.length}
      defaultOpen={defaultOpen}
      navigateTo={isPortal ? undefined : "/leads"}
      navigateLabel="Leads board"
      testId="block-leads"
      emptyHint="No leads attached to this customer."
    >
      <div className="divide-y divide-white/5 border border-white/5 rounded-md overflow-hidden">
        {leads.map((l) => (
          <div
            key={l.id}
            className="flex items-center gap-2 px-3 py-2 hover:bg-violet-500/5 transition-colors"
            data-testid={`leads-row-${l.id}`}
          >
            <Link
              to={isPortal ? "#" : `/leads/${l.id}`}
              className="flex-1 min-w-0 flex items-center justify-between gap-3 text-left"
              data-testid={`leads-row-link-${l.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{l.title}</div>
                <div className="text-[10px] text-[#647087] truncate flex items-center gap-2 mt-0.5">
                  {l.lead_source && <span>{l.lead_source}</span>}
                  {l.lead_rating ? (
                    <span className="flex items-center gap-0.5 text-amber-300">
                      <Star className="w-2.5 h-2.5 fill-amber-300" /> {l.lead_rating}
                    </span>
                  ) : null}
                  {l.value ? <span>R {Number(l.value).toLocaleString('en-GB')}</span> : null}
                  {l.external_ref && <span className="font-mono">{l.external_ref}</span>}
                </div>
              </div>
              <PhaseChip value={l.phase} />
            </Link>
            {canEdit && !isPortal && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDel(l); }}
                className="hv-btn hv-btn-ghost text-rose-300 px-2 py-1 shrink-0"
                title="Delete lead"
                data-testid={`leads-row-delete-${l.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        busy={busy}
        title={`Delete lead "${confirmDel?.title || ""}"?`}
        body="This removes the lead and all its comments. Calls and meetings stay on the customer."
        confirmLabel="Delete lead"
        confirmVariant="danger"
      />
    </FoldBlock>
  );
}
